<template>
    <div :class="[
        'gocc__page position--relative',
        itsNotAnErrorPage ? 'gocc__page--default' : 'gocc__page--error'
    ]">
        <operate
            v-if="showOperate"
            @close="showOperate = false"
        />
        <supervise
            v-if="showSupervise"
            @close="showSupervise = false"
        />
        <modal
            v-if="modal.show"
            :form="{
                class: 'operate__modal',
                settings: {
                    submissionType: 'none'
                }
            }"
            :customSettings="{
                color: 'green',
                class: 'operate__modal__content',
                button: {
                    label: 'confirm',
                    action: () => { callOperateMenu() }
                },
                show: {
                    infoSection: false
                }
            }"
            :executeCloseAction="!loading"
            :preventReset="true"
            @close="toggleModal()"
            :key="modal.show" >
            <template #modal__header >
                <h1 class="cblock__modal__header">{{ $t('accessibility.operate') }}</h1>
            </template>
            <template #modal__body >
                <base-field
                    class="operate__campaigns"
                    :settings="{
                        label: { class: 'capitalize', text: '' },
                        input: {
                            component: 'InputMultiOptions',
                            name: 'campaigns',
                            id: 'campaigns',
                            targetClass: 'field__input--default typography--default color--default',
                            value:[],
                            browserAutocomplete: 'off',
                            placeholder: $t('placeholders.select'),
                            isMissing: showError,
                            dynamicData: {
                                data: [ ...campaigns ],
                                ordering: campaignsLoading,
                                isEditPage: false
                            },
                            errorOnSubmit: {
                                show: errors.api.campaigns ? true : false,
                                messages: errors.api.campaigns
                            },
                            noListener: true
                        },
                        isCampaignList: true,
                        isEdition: false,
                        reset,
                        allowFilling: true
                    }"
                />
            </template>
        </modal>
        <block-header>
            <template #search-bar>
                <search v-if="showSearch" />
            </template>
        </block-header>
        <block-sidebar v-if="itsNotAnErrorPage" :fetch="fetch" />
        <div v-if="itsNotAnErrorPage" class="gocc__page__operations display--flex">
            <breadcrumbs />
            <div class="gocc__page__operations__actions display--flex">
                <button-with-icon
                    v-if="getters.can( 'operate', 'do' ) && getters.freeswitchCredentials"
                    :icon="{
                        component: () => import( /* webpackChunkName: 'IconOperate' */ '@/components/bundles/layout/images/icons/IconOperate' ),
                        class: 'gocc__page__operations__element gocc__page__operations__element--default'
                    }"
                    class="gocc__page__operations__operate"
                    :action="{
                        execute: true,
                        function: () => { toggleModal() }
                    }"
                />
                <button-with-icon
                    v-if="getters.can( 'operate', 'supervise' ) && getters.freeswitchCredentials"
                    :icon="{
                        component: () => import( /* webpackChunkName: 'IconSupervise' */ '@/components/bundles/layout/images/icons/IconSupervise' ),
                        class: 'gocc__page__operations__element gocc__page__operations__element--supervise'
                    }"
                    class="gocc__page__operations__supervise"
                    :action="{
                        execute: true,
                        function: () => { showSupervise = true }
                    }"
                />
            </div>
        </div>
        <transition
            name="fade"
            mode="out-in"
        >
            <div
                v-if="loading"
                class="gocc__page__loading position--absolute display--flex color--white"
            >
                <div class="gocc__page__loading__dots position--absolute">
                    <div class="gocc__page__loading__dot gocc__page__loading__dot--first position--absolute"></div>
                    <div class="gocc__page__loading__dot gocc__page__loading__dot--second position--absolute"></div>
                    <div class="gocc__page__loading__dot gocc__page__loading__dot--third position--absolute"></div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" version="1.1">
                    <defs>
                        <filter id="goey">
                            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
                            <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7" result="goo" />
                            <feBlend in="SourceGraphic" in2="goo" />
                        </filter>
                    </defs>
                </svg>
            </div>
            <router-view
                v-else
                v-bind="{
                    ...data,
                    hasLoading: loading,
                }"
                :key="$route.name"
            />
        </transition>
        <widget v-if="widgetHasContentFor" />
        <transition name="slide-up" appear-active-class="animated fadeInUp" appear >
            <action-bar
                v-if="$store.getters.isInEditMode"
                @close-bar="toggleActionBar( false )"
            />
        </transition>
    </div>
</template>

<script>
// packages
import {
    ref,
    onBeforeUnmount,
    watch,
    computed,
    reactive,
    toRefs
} from '@vue/composition-api';
import { Wormhole } from 'portal-vue';

// store
import { mapState, useStore, storePayload } from '@/hooks/use-store';

// hooks
import useModels from '@/hooks/use-models';
import { singleStructure } from '@/hooks/use-data';

// helpers
import { get } from '@/helpers/requests';

export default {
    components: {
        ...singleStructure.components,
        BlockHeader: () => import( /* webpackChunkName: 'Header' */ '@/components/bundles/layout/header/Header' ),
        BlockSidebar: () => import( /* webpackChunkName: 'Sidebar' */ '@/components/bundles/layout/sidebar/Sidebar' ),
        Breadcrumbs: () => import( /* webpackChunkName: 'Breadcrumbs' */ '@/components/bundles/layout/breadcrumbs/Breadcrumbs' ),
        Search: () => import( /* webpackChunkName: 'Search' */ '@/components/bundles/layout/search/Search' ),
        ActionBar: () => import( /* webpackChunkName: 'ActionBar' */ '@/components/bundles/bars/actions/ActionBar' ),
        Operate: () => import( /* webpackChunkName: 'Operate' */ '@/components/bundles/operate/Operate' ),
        Supervise: () => import( /* webpackChunkName: 'Supervise' */ '@/components/bundles/supervise/Supervise' ),
        ButtonWithIcon: () => import( /* webpackChunkName: 'ButtonWithIcon' */ '@/components/bundles/buttons/BaseButtonWithIcon' ),
        Widget: () => import( /* webpackChunkName: 'Widget' */ '@/components/bundles/layout/widget/Widget' ),
        Modal: () => import( /* webpackChunkName: 'Modal' */ '@/components/bundles/layout/modals/Modal' )
    },
    props: {
        ...singleStructure.props
    },
    setup ( props, { root }) {
        const { getters, actions } = useStore();

        const modal = reactive({
            show: false
        });
        const order = reactive({
            campaignsLoading: Boolean
        });
        const state = reactive({
            associateCampaigns: [],
            hasCampaigns: false,
            showOperate: false,
            showError: false
        });

        // Toggle da modal de escolha de campanha para operar
        const toggleModal = () => {
            actions( 'resetPayload' );
            modal.show = !modal.show;

            // bloqueia a barra de adição / edição
            actions( 'controlActionBar', { value: modal.show });

            if ( modal.show )
                getCampaignsRequest();
        };
        const getCampaignsRequest = ( async () => {
            order.campaignsLoading = true;
            requestData.campaigns = await get( '/campaigns?state[]=enabled&schedule=true' )
                .then( ({ data }) => data.content.data );
            requestData.campaigns = requestData.campaigns.map( campaign => ({ value: campaign.id, label: campaign.name }) );
            order.campaignsLoading = false;
        });
        // obtenção do payload & errors
        const store = reactive({
            payload: mapState({ module: 'storePayload', key: 'payload' }),
            helpers: mapState({ module: 'storePayload', key: 'helpers' }),
            blocks: mapState({ module: 'storePayload', key: 'blocks' })
        });

        const { errors, reset } = storePayload();

        // lista de campanhas
        const requestData = reactive({
            campaigns: []
        });

        // toggle da action bar
        const toggleActionBar = active => { actions( 'controlActionBar', { type: 'active', value: active }); };

        // detecta quando existe focus numa input
        const triggerBar = ({ element, toggleActive }) => {
            // validação se elemento em foco é de escuta e se
            // nenhum campo ainda está preenchido, de forma a evitar
            // a execução da acção para activar a bar.
            if (
                element.hasAttribute( 'data-listener' )
                && !getters.payloadHaveKeys
            )
                toggleActionBar( toggleActive );
        };

        // adicionado a DOM um listener, de forma a estar
        // atento quando existe focus numa input.
        document.addEventListener( 'focusin', event => { triggerBar({ element: event.target, toggleActive: true }); });
        document.addEventListener( 'focusout', event => { triggerBar({ element: event.target, toggleActive: false }); });

        // Caso o utilizador faça logout,
        // o listener na DOM é removido.
        onBeforeUnmount( () => {
            document.removeEventListener( 'focusin', triggerBar );
            document.removeEventListener( 'focusout', triggerBar );
        });

        const {
            loading,
            error,
            data,
            fetch
        } = useModels();

        // store - response
        const update = mapState({ module: 'storePayload', key: 'update' });

        watch( () => update.value,
            newUpdate => {
                if ( newUpdate )
                    fetch({ updateModel: true });
            });

        const showSupervise = ref( false );

        // show search
        const showSearch = computed( () => ![ 'index', 'reporting', 'notFound', 'newScript', 'script' ].includes( root.$route.name ) );

        // show breadcrumbs
        const itsNotAnErrorPage = computed( () => ![ 'notFound' ].includes( root.$route.name ) );

        // validação se apresenta o widget ou não
        const widgetHasContentFor = computed( () => Wormhole.hasContentFor( 'widget' ) );

        watch( () => store.payload,
            payload => {
                state.hasCampaigns = ( payload.campaigns && payload.campaigns.value !== '' );
            });
        watch( () => state.showOperate,
            newShowOperate => {
                if ( !newShowOperate ) {
                    // limpar payload
                    actions( 'resetPayload' );
                    // ativa action bar
                    actions( 'controlActionBar', { value: false });
                }
            });

        const callOperateMenu = () => {
            if ( state.hasCampaigns ) {
                state.showOperate = true;
                modal.show = false;
            } else
                state.showError = true;
        };

        return {
            loading,
            error,
            data,
            fetch,
            getters,
            toggleActionBar,
            showSupervise,
            showSearch,
            itsNotAnErrorPage,
            widgetHasContentFor,
            modal,
            toggleModal,
            errors,
            reset,
            callOperateMenu,
            ...toRefs( store ),
            ...toRefs( requestData ),
            ...toRefs( order ),
            ...toRefs( state )
        };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Animations
    // =============================================================================
    @include keyframes ( first-dot ) {
        40% {
            @include transform( scale( .7 ) );
        }
        50% {
            left: 45%;
        }
        55% {
            @include transform( scale( .7 ) );
        }
    }

    @include keyframes ( second-dot ) {
        40% {
            @include transform( scale( .7 ) );
        }
        50% {
            left: 55%;
        }
        55% {
            @include transform( scale( .7 ) );
        }
    }

    @include keyframes ( third-dot ) {
        40% {
            @include transform( scale( .7 ) );
        }
        55% {
            @include transform( scale( .7 ) );
        }
    }

    .slide-up-enter-active, .slide-up-leave-active {
        @include transform( translateY( 0 ) );
        @include transition( transform .3s ease-in-out, opacity .3s ease-in-out .1s );
    }

    .slide-up-enter, .slide-up-leave-to {
        @include transform( translateY( 200% ) );
        @include transition( opacity .3s ease-in-out, transform .3s ease-in-out .1s );
        opacity: 0;
        will-change: transform, opacity;
    }

    // =============================================================================
    // Styles
    // =============================================================================
    .gocc {
        &__page {
            min-height: 100vh;
            padding-top: $header-height;
            &--default {
                padding-bottom: 100px;
                margin: 0 213px 0 113px;
                @include responsive( max-width, 1480px ) {
                    & {
                        margin: 0 135px 0 26px;
                    }
                }
            }
            &--error {
                background: rgba( $color-green-light, .4 );
            }
            &__loading {
                @include transform-position( $header-height, false, false, -213px );
                align-items: center;
                justify-content: center;
                @include dimensions( calc( 100% + 323px ), calc( 100% - #{$header-height} ) );
                @include typography-specs( 30px, 56px );
                z-index: 3;
                backdrop-filter: blur( 3px );
                &__dots {
                    @include transform-position( 0, 0, 0, 0 );
                    filter: url('#goey')
                }
                &__dot {
                    @include transform-position( 50%, false, false, 50% );
                    @include dimensions( 100px, 100px );
                    @include rounded-corner( 50% );
                    background: $color-green;
                    &--first {
                        @include animation( first-dot cubic-bezier( 0.77, 0, 0.175, 1 ) 3.5s infinite );
                    }
                    &--second {
                        @include animation( second-dot cubic-bezier( 0.77, 0, 0.175, 1 ) 3.5s infinite );
                    }
                    &--third {
                        background-image: url( '~@/assets/images/logo.svg' );
                        background-repeat: no-repeat;
                        background-position: center;
                        background-size: 100%;
                        @include animation( third-dot cubic-bezier( 0.77, 0, 0.175, 1 ) 3.5s infinite );
                    }
                }
            }
            &__operations {
                align-items: center;
                justify-content: space-between;
                margin: 40px 0 30px;
                &__element {
                    @include dimensions( 55px, 55px );
                    &--default {
                        @include dimensions( 55px, 55px );
                    }
                    &--supervise {
                        @include dimensions( 45px, 45px );
                    }
                    .circle, .path, .path--both, .path--stroke {
                        @include transition( fill .3s ease-in-out );
                        will-change: fill;
                    }
                    &:hover {
                        .circle {
                            fill: $color-white;
                        }
                        .path {
                            fill: $color-green;
                            &--both {
                                fill: $color-green;
                                stroke: $color-green;
                            }
                            &--stroke {
                                stroke: $color-white;
                            }
                        }
                    }
                }
                &__actions {
                    gap: 10px;
                }
            }
            &__response {
                height: 40px;
                line-height: 40px;
            }
        }
    }
    .operate {
        &__modal {
            &--transition {
                &-enter-active, &-leave-active {
                    height: 412px;
                    opacity: 1;
                    overflow: hidden;
                    @include transition( height .3s ease-in-out, opacity .2s ease-in-out );
                }
                &-enter, &-leave-to {
                    height: 0;
                    opacity: 0;
                    @include transition( height .3s ease-in-out .2s, opacity .3s ease-in-out );
                    will-change: height, opacity;
                }
            }
            &__header {
                @include typography-specs( 25px, 33px, 700 );
            }
            &__content {
                max-width: 480px;
            }
            &__body {
                margin-top: 20px;
            }
            &__input {
                &--new-contact {
                    margin: 25px 0;
                }
            }
            &__icons {
                justify-content: center;
                margin-bottom: 45px;
                &__button {
                    @include transition( transfrom .3s ease-in-out );
                    will-change: transform;
                    &:active {
                        @include transform( scale( .8 ) );
                    }
                }
            }
            &__icon {
                @include transition( fill .3s ease-in-out );
                will-change: fill;
                &--reschedule {
                    margin: 0 20px;
                    &--active {
                        .circle {
                            fill: $color-green;
                        }
                        .path {
                            fill: $color-white;
                        }
                    }
                    .circle, .path {
                        @include transition( fill .3s ease-in-out );
                        will-change: fill;
                    }
                }
                &--green {
                    fill: rgba( $color-green, .6 );
                    &:hover {
                        fill: $color-green;
                        & .hand {
                            @include animation( thumbs-up .6s cubic-bezier( .73, .04, 1, .3 ) );
                        }
                    }
                    &--active {
                        fill: $color-green;
                    }
                }
                &--red {
                    fill: rgba( $color-red, .6 );
                    &:hover {
                        fill: $color-red;
                        & .hand {
                            @include animation( thumbs-down .6s cubic-bezier( .73, .04, 1, .3 ) );
                        }
                    }
                    &--active {
                        fill: $color-red;
                    }
                }
            }
        }
    }
</style>